import React, { useEffect, useRef, useState } from 'react'
import { Form } from 'semantic-ui-react'
import { useForm } from 'react-hook-form'
import { read, utils } from 'xlsx'
import { FlexRowBetween } from 'components'
import { FileUploadPicker } from 'components/FileUploadPicker'
import { Plus } from 'phosphor-react'
import { SubmitCancelButtons } from 'elements'
import { useNotification } from 'hooks/toast'
import {
    AcceptFileType,
    CommonUtility,
    InspectionService,
} from 'utility'
import { MasterAnswersService } from 'utility/services/master-answers'
import {
    AddResponseButton,
    MultiResponseList,
    ResponseEditorWrap,
    ResponseHintModal,
} from 'page-components/create-template/CreateResponseModal'
import {
    editorRowToApiPayload,
    emptyEditorRow,
    excelRowsFromValues,
    isNewMasterRow,
    masterItemToEditorRow,
} from './master-response-editor-utils'
import { getApiErrorMessage } from './utils'

const processExcelUploadResponse = data => {
    if (!Array.isArray(data)) return []
    return data
        .map(item => {
            if (item && typeof item.Value === 'string') return item.Value
            return null
        })
        .filter(Boolean)
}

const parseExcelColumn = (sheet, jsonData) =>
    jsonData
        .slice(1)
        .map((row, idx) => {
            const cellRef = `A${idx + 2}`
            const cell = sheet[cellRef]
            if (cell?.w) return cell.w
            const v = row[0]
            return v != null ? String(v) : ''
        })
        .filter(value => value && String(value).trim())

export const MasterAnswersItemsEditor = ({
    listId,
    initialItems = [],
    itemsLoading = false,
    onSaved,
}) => {
    const { successToast, errorToast: errorToastFn } = useNotification()
    const errorToastRef = useRef(errorToastFn)
    errorToastRef.current = errorToastFn
    const { control, handleSubmit } = useForm()
    const [rows, setRows] = useState([])
    const [saving, setSaving] = useState(false)
    const [files, setFiles] = useState([])
    const [fileError, setFileError] = useState(false)
    const [excelData, setExcelData] = useState([])
    const [suggestions, setSuggestions] = useState([])
    const [activeField, setActiveField] = useState(null)
    const [hintModalOpen, setHintModalOpen] = useState(false)
    const [currentHintIndex, setCurrentHintIndex] = useState(null)
    const [hintHeading, setHintHeading] = useState('')
    const [hintText, setHintText] = useState('')
    const [hintFiles, setHintFiles] = useState([])
    const [hintUploadLoading, setHintUploadLoading] = useState(false)
    const [hintHeadingError, setHintHeadingError] = useState('')

    useEffect(() => {
        if (!listId || itemsLoading) return
        const active = initialItems.filter(i => i.isActive)
        setRows(
            active.length
                ? active.map(masterItemToEditorRow)
                : [emptyEditorRow(0)],
        )
        setFiles([])
        setExcelData([])
        setSuggestions([])
    }, [listId, itemsLoading, initialItems])

    const visibleRows = rows.filter(r => !r._deleted)

    const onchange = (name, data, id) => {
        setRows(prev => {
            const next = [...prev]
            const index = next.findIndex(d => d.Id === id)
            if (index > -1) next[index] = { ...next[index], [name]: data }
            return next
        })
        setSuggestions([])
    }

    const setScoreFromQuestion = (responseId, isChecked) => {
        setRows(prev =>
            prev.map(resp => {
                if (resp.Id !== responseId) return resp
                return {
                    ...resp,
                    IsScoreOnThisResponse: isChecked,
                    Score: isChecked ? '' : resp.Score,
                }
            }),
        )
    }

    const handleDisplayTextChange = (value, id) => {
        setActiveField(id)
        if (value && excelData.length > 0) {
            const searchValue = value.toLowerCase().trim()
            setSuggestions(
                excelData.filter(item =>
                    item.toString().toLowerCase().includes(searchValue),
                ),
            )
        } else {
            setSuggestions([])
        }
        onchange('DisplayText', value, id)
    }

    const handleSuggestionClick = (suggestion, id) => {
        onchange('DisplayText', suggestion, id)
        setActiveField(null)
        setSuggestions([])
    }

    const addRow = () => {
        setRows(prev => [...prev, emptyEditorRow(visibleRows.length)])
    }

    const remove = id => {
        setRows(prev => {
            if (isNewMasterRow(id)) {
                return prev.filter(r => r.Id !== id)
            }
            return prev.map(r =>
                r.Id === id ? { ...r, _deleted: true } : r,
            )
        })
        setSuggestions([])
    }

    const onDragEnd = result => {
        if (!result.destination) return
        const items = CommonUtility.reorderList(
            visibleRows,
            result.source.index,
            result.destination.index,
        )
        const orderMap = new Map(items.map((r, i) => [r.Id, i]))
        setRows(prev =>
            prev.map(r => {
                if (r._deleted) return r
                const order = orderMap.get(r.Id)
                return order != null
                    ? { ...r, DisplayOrder: order }
                    : r
            }),
        )
    }

    const openHintModal = index => {
        const resp = visibleRows[index]
        if (!resp) return
        setCurrentHintIndex(rows.findIndex(r => r.Id === resp.Id))
        setHintHeading(resp.Hints?.[0]?.Heading || '')
        setHintText(resp.Hints?.[0]?.HintText || '')
        setHintFiles(
            resp.Hints?.[0]?.MediaPath
                ? [
                      {
                          url: resp.Hints[0].MediaPath,
                          id: resp.Id || Date.now(),
                          name: 'Media',
                          local: false,
                          type: 'image',
                      },
                  ]
                : [],
        )
        setHintModalOpen(true)
    }

    const closeHintModal = () => {
        setHintModalOpen(false)
        setCurrentHintIndex(null)
        setHintHeading('')
        setHintText('')
        setHintFiles([])
        setHintHeadingError('')
    }

    const saveHint = () => {
        if (!hintHeading.trim()) {
            setHintHeadingError('Heading is required')
            return
        }
        setHintHeadingError('')
        if (currentHintIndex != null && currentHintIndex >= 0) {
            setRows(prev => {
                const next = [...prev]
                next[currentHintIndex] = {
                    ...next[currentHintIndex],
                    Hints: [
                        {
                            Heading: hintHeading,
                            HintText: hintText,
                            MediaPath:
                                hintFiles[0]?.url ||
                                hintFiles[0]?.FileURL ||
                                '',
                        },
                    ],
                }
                return next
            })
        }
        closeHintModal()
    }

    const onHintFileUpload = async uploadedFiles => {
        setHintUploadLoading(true)
        try {
            const uploadPromises = uploadedFiles.map(async file => {
                if (file.local) {
                    const fileToUpload = file.file
                    const uploadResponse = await InspectionService.uploadMedia({
                        media: fileToUpload,
                        type: fileToUpload.type,
                    })
                    const mediaPayload = [
                        {
                            MediaId: 0,
                            FAQId: '',
                            Order: 1,
                            FolderName: '1',
                            Comment: uploadResponse?.Comment || '',
                            Caption: uploadResponse?.Caption || '',
                            FileType:
                                uploadResponse?.FileType ||
                                fileToUpload.type ||
                                '',
                            SignedURL:
                                uploadResponse?.ImagePathUrl ||
                                uploadResponse?.FileURL ||
                                uploadResponse?.SignedURL,
                            FileURL:
                                uploadResponse?.ImagePathUrl ||
                                uploadResponse?.FileURL ||
                                uploadResponse?.SignedURL,
                            FileKey:
                                uploadResponse?.CloudImageId ||
                                uploadResponse?.FileKey,
                        },
                    ]
                    const savedMedia = await InspectionService.saveMedia(
                        mediaPayload,
                    )
                    const s3Url =
                        savedMedia[0].FileURL ||
                        savedMedia[0].SignedURL ||
                        uploadResponse.FileURL ||
                        uploadResponse.SignedURL
                    const savedFile = {
                        ...savedMedia[0],
                        id: savedMedia[0].MediaId,
                        name:
                            savedMedia[0].FileKey ||
                            uploadResponse.FileKey ||
                            fileToUpload.name ||
                            'File',
                        url: s3Url,
                        local: false,
                        type: fileToUpload.type,
                    }
                    setHintFiles([savedFile])
                    return savedFile
                }
                return file
            })
            await Promise.all(uploadPromises)
        } catch {
            errorToastRef.current('Could not upload hint media')
        } finally {
            setHintUploadLoading(false)
        }
    }

    const applyExcelValues = columnData => {
        if (!columnData.length) {
            setFileError('No valid data found in the Excel file')
            return
        }
        setExcelData(columnData)
        setSuggestions([])
        setRows(excelRowsFromValues(columnData))
        successToast(`Loaded ${columnData.length} row(s) from file`)
    }

    const onFileUpload = async file => {
        setFiles(file)
        setFileError(false)
        try {
            const uploadResponse = await InspectionService.uploadMedia({
                media: file[0].file,
                callfrom: 1,
                type: 'xlsx',
                isExcel: true,
            })

            if (!uploadResponse) {
                const reader = new FileReader()
                reader.onload = e => {
                    try {
                        const data = new Uint8Array(e.target.result)
                        const workbook = read(data, { type: 'array' })
                        const firstSheet =
                            workbook.Sheets[workbook.SheetNames[0]]
                        const jsonData = utils.sheet_to_json(firstSheet, {
                            header: 1,
                            raw: true,
                        })
                        applyExcelValues(parseExcelColumn(firstSheet, jsonData))
                    } catch {
                        setFileError('Error parsing Excel file')
                    }
                }
                reader.onerror = () => setFileError('Error reading file')
                reader.readAsArrayBuffer(file[0].file)
            } else {
                const columnData = processExcelUploadResponse(uploadResponse)
                applyExcelValues(columnData)
            }
        } catch {
            setFileError('Error processing the file')
        }
    }

    const submit = async () => {
        if (!listId) return
        const active = rows.filter(r => !r._deleted)
        const payloads = active
            .map((row, index) => ({
                row,
                payload: editorRowToApiPayload(row, index),
            }))
            .filter(x => x.payload)

        if (!payloads.length) {
            errorToastRef.current('Add at least one response with display text')
            return
        }

        setSaving(true)
        try {
            await Promise.all(
                payloads.map(({ row, payload }) =>
                    isNewMasterRow(row.Id)
                        ? MasterAnswersService.createItem(listId, payload)
                        : MasterAnswersService.updateItem(
                              listId,
                              row.Id,
                              payload,
                          ),
                ),
            )

            const deleted = rows.filter(
                r => r._deleted && !isNewMasterRow(r.Id),
            )
            await Promise.all(
                deleted.map(row =>
                    MasterAnswersService.deleteItem(listId, row.Id),
                ),
            )

            successToast('Responses saved')
            onSaved?.()
        } catch (err) {
            errorToastRef.current(getApiErrorMessage(err))
        } finally {
            setSaving(false)
        }
    }

    if (itemsLoading) {
        return <p style={{ color: '#6b7280', margin: 0 }}>Loading items…</p>
    }

    return (
        <>
            <ResponseEditorWrap>
                <Form noValidate>
                    <FlexRowBetween className="mt-0 mb-4">
                        <div className="col-4" />
                        <div className="text-align-end">
                            <FileUploadPicker
                                maxFiles={1}
                                maxFileSize={500}
                                accept={AcceptFileType.xlsx}
                                addedNewFiles={onFileUpload}
                                files={files}
                                error={fileError}
                                tagPreview
                                showTakePicture={false}
                                uploadButtonText="Upload File"
                            />
                        </div>
                    </FlexRowBetween>

                    <MultiResponseList
                        list={visibleRows}
                        remove={remove}
                        control={control}
                        errors={{}}
                        onchange={onchange}
                        onDragEnd={onDragEnd}
                        setScoreFromQuestion={setScoreFromQuestion}
                        handleDisplayTextChange={handleDisplayTextChange}
                        suggestions={suggestions}
                        handleSuggestionClick={handleSuggestionClick}
                        activeField={activeField}
                        onOpenHintModal={openHintModal}
                    />

                    <AddResponseButton onClick={addRow}>
                        <Plus size={24} />
                        <span className="mx-4">Create Response</span>
                    </AddResponseButton>

                    <SubmitCancelButtons
                        className="mt-4"
                        onSubmit={handleSubmit(submit)}
                        loading={saving}
                        hideCancel
                    />
                </Form>
            </ResponseEditorWrap>

            <ResponseHintModal
                open={hintModalOpen}
                onClose={closeHintModal}
                hintHeading={hintHeading}
                onHintHeadingChange={value => {
                    setHintHeading(value)
                    if (hintHeadingError) setHintHeadingError('')
                }}
                hintText={hintText}
                onHintTextChange={setHintText}
                hintHeadingError={hintHeadingError}
                hintFiles={hintFiles}
                onHintFileUpload={onHintFileUpload}
                onRemoveHintFile={() => setHintFiles([])}
                hintUploadLoading={hintUploadLoading}
                onSave={saveHint}
            />
        </>
    )
}
